/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import Box from '@mui/material/Box';
import { useTheme } from '@emotion/react';
import PhoneNumberTextField from '@PhoneNumberTextField';
import { useEffect, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'intl-tel-input/build/css/intlTelInput.css';
import { TRANSITION_TIMEOUT_TIME } from '../../Data/Constants';
import ValidationStatus from '../../Data/ValidationStatus';
import SagaStatus from '../../Data/SagaStatus';
import { getFirstEditableComponentOnPage } from '../../Helpers/components';
import {
  clearConstraintsValidations,
  setBrowserAutoFilledActive,
  setConstraintMessage,
  updateValue,
} from '../../Lib/redux/slices/components';
import { setPhoneNumberCountryCode } from '../../Lib/redux/slices/formConfig';
import {
  setLegalStatement,
  updateIsLegalStatementRevealed,
  updateShouldRenderLegalStatement,
} from '../../Lib/redux/slices/legalStatement';
import { setShouldNavigateForward } from '../../Lib/redux/slices/navigationControls';
import { setComponentIdToReceiveFocus } from '../../Lib/redux/slices/persistKeyboard';
import QuestionText from '../QuestionText/QuestionText';
import {
  getPhoneNumberCountryCode,
  getPhoneNumberErrorMessage,
  getPhonePlugin,
  getPhonePluginInput,
} from './phoneNumberInput.helpers';
import validatePhone from './validatePhone.helper';
import makePhoneNumberInputStyling from './usePhoneNumberInputStyles';
import Subcategories from '../../Data/Subcategories';
import { isLastPageBeforeSubmission } from '../../Hooks/useNavigation/useNavigation.helpers';
import { setIsFinalPageBeforeSubmission } from '../../Lib/redux/slices/navigation';
import { SELECT_COUNTRY_MAPPED_QUESTION_ID } from '../../Data/Country';
import { useAutoFocusFirstEditableComponent } from '../../Hooks/useAutoFocusFirstEditableComponent';
import { renderMustache } from '../../Helpers/mustache';
import { ComponentTypesToReceiveFocusOnPageEnter } from '../../Data/Components';

const PhoneNumberInput = ({
  componentId,
  displayQuestionTextInsideTextfield,
  errorMessage,
  iFrameId,
  componentStatus,
  placeholder,
  subheading,
  text,
  value,
  isAutoFilledActive,
  formId,
}) => {
  const dispatch = useDispatch();
  const styling = makePhoneNumberInputStyling(useTheme());
  const component = useSelector(
    (i) => i.components[`component_${componentId}`]
  );
  const isAnswered =
    componentStatus === ValidationStatus.VALID ||
    componentStatus === ValidationStatus.INVALID;
  const [isAutoFilled, setIsAutoFilled] = useState(false);

  const {
    phoneNumberCountryCode,
    countryCode,
    isSafari,
    isIos,
    isAndroid,
    subcategoryId,
    submissionPageNumber,
  } = useSelector((i) => i.formConfig);
  const { dynamicNewsletterName } = useSelector((store) => store.newsletter);
  const components = useSelector((store) => store.components);
  const { pages } = useSelector((store) => store.paging);
  const { currentPage } = useSelector((store) => store.navigation);
  const { componentIdToReceiveFocus } = useSelector(
    (store) => store.persistKeyboard
  );
  const { componentIdToSagaStatus } = useSelector(
    (store) => store.dataMappings
  );
  const [attemptNavigate, setAttemptNavigate] = useState(false);
  const handleChange = (params) => {
    dispatch(updateValue({ componentId, value: params.value }));
  };

  const triggerPhoneValidation = (
    currentPhoneNumberCountryCode,
    numberValue
  ) => {
    validatePhone({
      componentId,
      countryCode: currentPhoneNumberCountryCode,
      dispatch,
      iFrameId,
      constraints: component.validation?.constraints,
      formId,
      value: numberValue,
    });
  };

  const handleBlur = () => {
    if (value === '' || value === null) {
      return;
    }
    triggerPhoneValidation(phoneNumberCountryCode, value);
    if (isAutoFilledActive) {
      dispatch(setBrowserAutoFilledActive({ componentId, value: false }));
    }
  };

  const updatePhoneNumberErrorMessage = (currentPhoneNumberCountryCode) => {
    dispatch(
      setConstraintMessage({
        componentId,
        constraint: 'phone',
        message: getPhoneNumberErrorMessage(currentPhoneNumberCountryCode),
      })
    );
  };

  const handleCountryCodeChange = () => {
    const currentPhoneNumberCountryCode =
      getPhoneNumberCountryCode(componentId);
    if (currentPhoneNumberCountryCode !== '') {
      updatePhoneNumberErrorMessage(currentPhoneNumberCountryCode);
      batch(() => {
        dispatch(
          setPhoneNumberCountryCode({
            phoneNumberCountryCode: currentPhoneNumberCountryCode,
          })
        );

        const selectedCountryIsUSA = Object.values(components).find(
          (componentFromList) => {
            return (
              componentFromList.component.mappedQuestionId ===
                SELECT_COUNTRY_MAPPED_QUESTION_ID &&
              componentFromList.input?.value?.trim() === 'USA'
            );
          }
        );

        if (
          countryCode !== 'US' &&
          !(
            subcategoryId === Subcategories.REMOVALS_AUTOQUALIFIED.id &&
            selectedCountryIsUSA
          )
        ) {
          // We don't re-request a statement for US forms, but for others, here we invalidate the legalStatement so a new one is requested in LegalStatement
          dispatch(setLegalStatement({ statement: null }));
        }

        if (subcategoryId === Subcategories.REMOVALS_AUTOQUALIFIED.id) {
          const isFinalPageBeforeSubmissionValue = isLastPageBeforeSubmission({
            page: currentPage,
            components,
            pages,
            submissionPageNumber,
            dynamicNewsletterName,
            phoneNumberCountryCode: currentPhoneNumberCountryCode,
            subcategoryId,
          });

          // This is sufficient to get the legal statement visibility to change with Dynamic height turned off
          dispatch(
            setIsFinalPageBeforeSubmission({
              isFinalPageBeforeSubmission: isFinalPageBeforeSubmissionValue,
            })
          );

          // If dynamic height is turned on, we need to update this field too otherwise the legal statement will not budge
          const shouldRenderLegalStatementValue =
            isFinalPageBeforeSubmissionValue;
          dispatch(
            updateShouldRenderLegalStatement({
              shouldRenderLegalStatement: shouldRenderLegalStatementValue,
            })
          );
          dispatch(
            updateIsLegalStatementRevealed({
              isLegalStatementRevealed: shouldRenderLegalStatementValue,
            })
          );
        }
      });

      triggerPhoneValidation(currentPhoneNumberCountryCode, value);
    }
  };

  useEffect(() => {
    const input = getPhonePluginInput(componentId);
    const plugin = getPhonePlugin(componentId);
    if (plugin) {
      plugin.setCountry(phoneNumberCountryCode);
    }
    updatePhoneNumberErrorMessage(phoneNumberCountryCode);
    if (value && value !== '') {
      triggerPhoneValidation(phoneNumberCountryCode, value);
    }
    input.addEventListener('countrychange', handleCountryCodeChange);
  }, []);

  const handleClick = () => {
    dispatch(clearConstraintsValidations({ componentId }));
  };

  const handleEnterPress = (event) => {
    if (event.key === 'Enter') {
      const firstEditableComponentOnNextPage = getFirstEditableComponentOnPage(
        component.component.page + 1,
        components,
        pages
      );

      if (
        (isIos || isAndroid) &&
        ComponentTypesToReceiveFocusOnPageEnter.includes(
          firstEditableComponentOnNextPage?.input?.type
        )
      ) {
        dispatch(
          setComponentIdToReceiveFocus({
            componentIdToReceiveFocus:
              firstEditableComponentOnNextPage.component.id,
          })
        );
      }
      triggerPhoneValidation(phoneNumberCountryCode, value);
      setAttemptNavigate(true);
    }
  };

  const handleKeyDown = (event) => {
    // Browser auto fill currently triggers key down events with the following key and code
    if (event.key === 'Unidentified' && event.code === undefined) {
      setIsAutoFilled(true);
    } else {
      setIsAutoFilled(false);
    }
  };

  useEffect(() => {
    // We usually trigger a validation on blur of the question but if it is filled by autofill then we will not see this blur. Therefore we will instead trigger the validation immediately.
    if (isAutoFilled) {
      triggerPhoneValidation(phoneNumberCountryCode, value);
      setIsAutoFilled(false);
    }
  }, [value]);

  // sagaStatus is only needed if the phone plugin did not load and we fallback to validation via the gateway
  const sagaStatus = componentIdToSagaStatus?.[componentId];

  useEffect(() => {
    // Only proceed if we're not in a pending state for this component
    const isValidationComplete =
      !sagaStatus || sagaStatus === SagaStatus.COMPLETE;

    if (
      componentStatus === ValidationStatus.VALID &&
      attemptNavigate &&
      isValidationComplete
    ) {
      setAttemptNavigate(false);
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }
    if (
      componentStatus === ValidationStatus.INVALID &&
      attemptNavigate &&
      isValidationComplete
    ) {
      setAttemptNavigate(false);
    }
  }, [attemptNavigate, componentStatus, componentId, sagaStatus, dispatch]);

  const inputRef = useRef(null);
  const hiddenInputRef = useRef(null);
  useAutoFocusFirstEditableComponent({ componentId, inputRef });

  useEffect(() => {
    if (inputRef.current && componentIdToReceiveFocus === componentId) {
      hiddenInputRef.current.focus({ preventScroll: true });
      // We need to wait for the transition to finish before focusing on the input
      // The enterPhase/exitPhase state values overlap on component mount in the middle of the transition (i.e. both are false)
      // So we need to wait for the transition time + 100ms to ensure the transition has finished, otherwise the phone plugin causes glitchy UI behaviour
      setTimeout(() => {
        inputRef.current.focus({ preventScroll: true });
      }, TRANSITION_TIMEOUT_TIME + 100);
      dispatch(
        setComponentIdToReceiveFocus({ componentIdToReceiveFocus: null })
      );
    }
  }, [componentIdToReceiveFocus]);

  return (
    <Box data-testid="phoneBox" onKeyDown={handleEnterPress} css={styling.box}>
      {!displayQuestionTextInsideTextfield && (
        <QuestionText
          heading={renderMustache({
            template: text,
            components,
            currentComponent: component,
            iFrameId,
            formId,
          })}
          subheading={renderMustache({
            template: subheading,
            components,
            currentComponent: component,
            iFrameId,
            formId,
          })}
        />
      )}
      <input
        type="text"
        ref={hiddenInputRef}
        id="hidden-input-for-persistent-keyboard"
        css={styling.hiddenInputForPersistentKeyboard}
      />
      <Box css={styling.textFieldBox}>
        <PhoneNumberTextField
          autoComplete="section-parent tel"
          errorMessage={errorMessage}
          id={`phoneNumberTextField-${componentId}`}
          isAnswered={isAnswered}
          isAutoFilledActive={isAutoFilledActive}
          isSafari={isSafari}
          label={!displayQuestionTextInsideTextfield ? '' : text}
          value={value}
          onChange={handleChange}
          onClick={handleClick}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          type="tel"
          placeholder={placeholder}
          inputRef={inputRef}
          inputProps={{
            'data-testid': 'phoneNumberTextField',
          }}
        />
      </Box>
    </Box>
  );
};

export default PhoneNumberInput;
